import { useState } from "react";

import {
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { API_URL } from "../config/api";
import type { LicencePrefill } from "../models/LicencePrefill";
import { logActivity } from "../services/activity";

type Props = {
    open: boolean;
    onClose: () => void;
    onFound: (prefill: LicencePrefill) => void;
};

// Recherche dans le listing officiel de la fédération : le n° de licence
// et les 2 premières lettres du prénom doivent correspondre.
export default function LicenseLookupDialog({
    open,
    onClose,
    onFound,
}: Props) {
    const [licence, setLicence] = useState("");
    const [firstNameStart, setFirstNameStart] = useState("");
    const [error, setError] = useState("");
    const [searching, setSearching] = useState(false);

    const reset = () => {
        setLicence("");
        setFirstNameStart("");
        setError("");
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const canSearch =
        !searching && licence.length === 6 && firstNameStart.length === 2;

    const search = async () => {
        if (!canSearch) {
            return;
        }

        setSearching(true);
        setError("");

        try {
            const response = await fetch(`${API_URL}/licence/lookup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ licence, firstNameStart }),
            });
            const body = await response.json();

            if (!response.ok) {
                throw new Error(body.error ?? "Recherche impossible");
            }

            reset();
            logActivity("licence_lookup_success");
            onFound(body);
        } catch (err) {
            const message =
                err instanceof Error ? err.message : "Recherche impossible";

            setError(message);
            logActivity("licence_lookup_failed", { error: message });
        } finally {
            setSearching(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>
                Entrer un n° de licence
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    Pour les judokas affiliés à la Fédération Wallonie-Bruxelles.
                    Le formulaire d'inscription sera pré-rempli.
                </Typography>

                <Stack
                    component="form"
                    spacing={2}
                    sx={{ mt: 2 }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        search();
                    }}
                >
                    <TextField
                        autoFocus
                        fullWidth
                        label="N° de licence"
                        value={licence}
                        slotProps={{
                            htmlInput: {
                                inputMode: "numeric",
                                maxLength: 6,
                            },
                        }}
                        onChange={(e) =>
                            setLicence(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                    />

                    <TextField
                        fullWidth
                        label="2 premières lettres du prénom"
                        value={firstNameStart}
                        slotProps={{
                            htmlInput: {
                                maxLength: 2,
                                autoCapitalize: "characters",
                            },
                        }}
                        onChange={(e) =>
                            setFirstNameStart(
                                e.target.value
                                    .replace(/[^\p{L}]/gu, "")
                                    .slice(0, 2)
                                    .toUpperCase()
                            )
                        }
                    />

                    {error && <Alert severity="error">{error}</Alert>}

                    {/* Permet de valider avec la touche Entrée */}
                    <button type="submit" hidden />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>
                    Annuler
                </Button>

                <Button
                    variant="contained"
                    color="error"
                    disabled={!canSearch}
                    onClick={search}
                >
                    {searching ? "Recherche..." : "Rechercher"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
